import { useMemo } from 'react';
import { useMedicationSearch } from './use-medication-search';
import { useMedicationValueSetSearch } from './use-medication-valueset-search';
import type { MedicationOption } from '../lib/to-medication-result';

/**
 * Medication search across BOTH tiers — the provider's favourites, then the whole catalogue.
 *
 * ── THE DEFECT ────────────────────────────────────────────────────────────────────────────────
 *
 * Measured: **0 of 22 dictated drug names resolved.** A clinician dictates "azithromycin" and the
 * picker returns nothing, so an ambient medication proposal cannot be accepted at all.
 *
 * The cause is not a missing vocabulary layer, which is what two earlier readings assumed. It is a
 * single-tier search. `GET /api/Provider/GetMedication` is the FAVOURITES endpoint — deliberately,
 * confirmed by Andrew — and it matches one column with one substring:
 *
 *     x.Provider == providerId && x.Display.ToLower().Contains(searchQuery.ToLower())
 *
 * So a drug the clinician has never favourited returns nothing, however good the master search is.
 *
 * ── AND THE MASTER SEARCH IS ALREADY GOOD ─────────────────────────────────────────────────────
 *
 * `SearchValueSets2` searches `Code`, `Display` AND `LongDescription` with the query token-split
 * (Andrew's commit `bf8b55b`, 26 Aug 2022 — four years old and never pointed at). `LongDescription`
 * carries BOTH DDC names:
 *
 *     CIPRO-SOL - CIPROFLOXACIN [200 MG/100ML] - SOLUTION FOR INFUSION (100ML) - ... , SAUDI ARABIA
 *
 * So **brand↔generic resolution already works today**. Andrew's measurement, 35 generic terms,
 * products found on `Display`-only versus the master query:
 *
 *     AZITHROMYCIN   0 ->  95      IBUPROFEN     1 -> 140      PARACETAMOL  10 -> 255
 *     ROSUVASTATIN   0 -> 127      AMOXICILLIN   1 -> 219      CETIRIZINE    0 ->  71
 *
 * **Eight of 35 return ZERO on `Display`-only before favourites scoping is even applied.** Nothing
 * to import, no synonym curation, no Master change — this is a routing fix and only a routing fix.
 *
 * ── WHY BOTH TIERS AND NOT A REPLACEMENT ──────────────────────────────────────────────────────
 *
 * The favourites endpoint is not wrong, it is scoped. A clinician's own list is the right first
 * answer — it is short, it is theirs, and it is what the A/P rail was built around. Replacing it
 * with the catalogue would bury a five-item personal list under 255 paracetamol products. So
 * favourites come FIRST and the catalogue fills in behind them, which is the shape labs and
 * radiology already use.
 *
 * ── THE CAVEAT THAT SURVIVES THIS FIX ─────────────────────────────────────────────────────────
 *
 * 504 `egyptian-drug-guide` rows have `GenericName` NULL and `Display` == `LongDescription` == a
 * bare brand stub ("Celebrex", "Mobic"). **Generic search is impossible for those rows by any
 * route**, and this change does not fix them. Recorded so the fix is not oversold: DDC is fine,
 * that 504 is not.
 */

/** Favourites first, then catalogue rows the favourites do not already cover. */
export function mergeTiers(
  favourites: readonly MedicationOption[] | undefined,
  catalogue: readonly MedicationOption[] | undefined,
): MedicationOption[] {
  const first = favourites ?? [];
  // Keyed on `code`, which is the medication's identity — NOT on `name`, because the same drug
  // appears under many trade names and de-duplicating by display would hide real distinct products.
  const seen = new Set(first.map((m) => m.code).filter(Boolean));
  const rest = (catalogue ?? []).filter((m) => !m.code || !seen.has(m.code));
  return [...first, ...rest];
}

/** Both tiers' own minimum (use-medication-search.ts, use-medication-valueset-search.ts). */
const MIN_QUERY_LENGTH = 2;

/**
 * @param providerId scopes the favourites tier. Absent means no favourites — the catalogue alone,
 *                   which is the correct degraded behaviour rather than an empty result.
 */
export function useMedicationSearchTiered(
  providerId: string | null | undefined,
  query: string,
) {
  const favourites = useMedicationSearch(providerId, query);
  const catalogue = useMedicationValueSetSearch(query);

  // The catalogue keeps its previous query's rows as placeholder data, both while disabled (below
  // the minimum) and while the next query loads ("drug" → "d" → "do"). Those rows answer a search
  // the clinician is no longer making, so only rows fetched for THIS query are listed.
  const searchable = query.trim().length >= MIN_QUERY_LENGTH;
  const catalogueRows = catalogue.isPlaceholderData ? undefined : catalogue.data;
  const data = useMemo(
    () => (searchable ? mergeTiers(favourites.data, catalogueRows) : []),
    [searchable, favourites.data, catalogueRows],
  );

  return {
    data,
    // Loading only while NEITHER tier has answered. A clinician should see their favourites the
    // moment they arrive rather than waiting on a 255-row catalogue query to settle.
    isLoading: favourites.isLoading && catalogue.isLoading,
    // Either tier still in flight (the A&P rail's "Searching…", same signal its other categories use).
    isFetching: favourites.isFetching || catalogue.isFetching,
    // An error is reported only when BOTH tiers failed. One tier down is a degraded search, not a
    // broken one, and showing an error over a usable list would send the clinician to type it by
    // hand when half the answer was on screen.
    isError: favourites.isError && catalogue.isError,
    favouritesCount: favourites.data?.length ?? 0,
    catalogueCount: catalogue.data?.length ?? 0,
  };
}
